// src/contexts/ThemeContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const ThemeContext = createContext();

const THEME_STORAGE_KEY = "risk360-theme";

/**
 * Get the initial theme based on:
 * 1. User's stored preference (localStorage)
 * 2. System preference (prefers-color-scheme)
 * 3. Default to 'dark' if neither available
 */
const getInitialTheme = () => {
  // Check localStorage first
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return stored;
    }

    // Check system preference
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches) {
      return "light";
    }
  }

  // Default to light theme (dark mode still needs work)
  return "light";
};

/**
 * Apply theme class to document root
 * Dark theme = no class (default)
 * Light theme = 'light' class
 */
const applyTheme = (theme) => {
  const root = document.documentElement;
  if (theme === "light") {
    root.classList.add("light");
  } else {
    root.classList.remove("light");
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(getInitialTheme);
  const [initialized, setInitialized] = useState(false);

  // Apply theme on mount and when theme changes
  useEffect(() => {
    applyTheme(theme);
    setInitialized(true);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: light)");

    const handleChange = (e) => {
      // Only follow system preference if user hasn't set a preference
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (!stored) {
        setTheme(e.matches ? "light" : "dark");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      localStorage.setItem(THEME_STORAGE_KEY, next);
      return next;
    });
  }, []);

  const setThemeMode = useCallback((newTheme) => {
    if (newTheme === "light" || newTheme === "dark") {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
      setTheme(newTheme);
    }
  }, []);

  const isDark = theme === "dark";
  const isLight = theme === "light";

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
        setTheme: setThemeMode,
        isDark,
        isLight,
        initialized,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
